import { Scales } from '@mentimeter/visualizations-react';
import { FullSizeContainer } from '@mentimeter/visualizations-react/utils/full-size-container';
import { IdentitiesListModal } from '@mentimeter/slides-presentation-ui/identified-responses/modal';
import { IdentitiesList } from '@mentimeter/slides-presentation-ui/identified-responses/list';
import { Box } from '@mentimeter/ragnar-ui/box';
import { useRagnar } from '@mentimeter/ragnar-react';
import { trackUser } from '@api/tracking/client';
import { useTranslations } from 'next-intl';
import {
  useIdentifiedResultsListState,
  type IdentitiesListState,
} from '../utils/useIdentifiedResultsListState';
import type { ScalesVisualizationDataHandlerProps } from '../types';
import type { OpenedIdentifiedResponsesListEvent } from '../../choices/types';

export const ScalesVisualizationDataHandler = ({
  results,
  range,
  choices,
  dimensionLabels,
  shouldShowOverallAverage,
  shouldHideResults,
  shouldAnimate,
  renderChoiceVisualization,
  renderChoiceLabel,
}: ScalesVisualizationDataHandlerProps) => {
  const {
    theme: {
      visualizationColors: { fillColors, textColor },
      colors: { bgStrong, bg },
      fonts: { body },
    },
  } = useRagnar();

  const t = useTranslations('presenting.scales');

  const colors = fillColors.length > 0 ? fillColors : [textColor];

  const {
    identitiesListState,
    setIdentitiesListState,
    identityListChoice,
    identityListChoiceIndex,
    activeScaleValueIdentities,
  } = useIdentifiedResultsListState({
    choices,
    results,
    shouldHideResults,
  });

  const handleShowIdentitiesList = (
    identitiesListState: IdentitiesListState,
  ) => {
    if (identitiesListState.choiceId) {
      trackUser({
        event: 'Opened identified responses list',
        properties: {
          'slide type': 'scales',
          visualization: 'scales',
          placement: 'Visualization',
          context: 'Presentation view',
        },
      } satisfies OpenedIdentifiedResponsesListEvent);
    }
    setIdentitiesListState(identitiesListState);
  };

  const shouldShowIdentitiesList = Boolean(
    identityListChoice !== undefined &&
      activeScaleValueIdentities?.participants?.length &&
      activeScaleValueIdentities.participants?.length > 0 &&
      results.length > 0,
  );

  const activeIdentitiesList = activeScaleValueIdentities?.participants?.map(
    (participant) => ({
      ...participant,
      displayName: participant.displayName || 'unknown',
    }),
  );

  const visualizationChoices = choices.map((choice) => ({
    id: choice.interactiveContentChoiceId,
    label: choice.title,
  }));

  return (
    <Box width="100%" height="100%" px="4%">
      <FullSizeContainer>
        {(width, height) => (
          <Scales
            width={width}
            height={height}
            staticOptions={visualizationChoices}
            rangeMax={range.max}
            rangeMin={range.min}
            labelAccessor={(d) => d.label}
            valueAccessor={(d) => d.score}
            textColor={textColor}
            data={results}
            rangeMinLabel={dimensionLabels.min}
            rangeMaxLabel={dimensionLabels.max}
            colors={colors as [string, ...[string]]}
            backgroundColor={bg}
            overallAverageColor={bgStrong}
            isShowAverageEnabled={shouldShowOverallAverage}
            disableAnimations={!shouldAnimate}
            onScaleValueClick={
              results === undefined ? undefined : handleShowIdentitiesList
            }
            activeScale={identitiesListState}
            fontFamily={body}
            renderChoiceVisualization={renderChoiceVisualization}
            renderChoiceLabel={renderChoiceLabel}
          />
        )}
      </FullSizeContainer>
      {shouldShowIdentitiesList && (
        <IdentitiesListModal
          onDismiss={() =>
            setIdentitiesListState({
              choiceId: null,
              value: null,
            })
          }
        >
          <IdentitiesList
            identities={activeIdentitiesList ?? []}
            color={
              fillColors[identityListChoiceIndex % fillColors.length] ?? '#000'
            }
            title={t('identity_list_rating_option_title', {
              rating: `${identitiesListState.value}`,
            })}
            totalResponses={activeScaleValueIdentities?.responseCount ?? 0}
          />
        </IdentitiesListModal>
      )}
    </Box>
  );
};
